import React from "react";
import { Box, Image, Text, Flex, Button, Icon } from "@chakra-ui/react";
import { FaRegClock, FaRegUser, FaRegClone } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const SkillCourseCard = ({ course }) => {
  const navigate = useNavigate();

  // Extract data from course object
  const {
    image = course.course_thumbnail || course.image,
    title = course.batch_name || course.title,
    batch_type = course.batch_type || "",
    course_name = course.course_name,
    amount = course.amount,
    deadline = course.deadline || course.end_date,
    venue = course.venue_name,
    batch_status = course.batch_status,
  } = course;

  // Set value based on batch_type
  const batch_type_label = batch_type.toLowerCase() === "free" ? "Free" : batch_type.toLowerCase() === "paid" ? "Premium" : batch_type;
  // Format deadline to show only date
  const formatDate = (dateString) => {
    if (!dateString || dateString === "TBA") return "TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format duration (total hours)
  const formatDuration = (duration) => {
    if (!duration || duration === "TBA") return "TBA";
    // If duration is in HH:MM format, convert to hours
    if (duration.includes(":")) {
      const [hours, minutes] = duration.split(":").map(Number);
      const totalHours = hours + minutes / 60;
      return totalHours === 1 ? "1 hour" : `${totalHours.toFixed(1)} hours`;
    }
    return duration;
  };

  // Get additional course information
  const totalHours = formatDuration(course.duration);

  const handleClick = () => {
    // Use training_id directly for URL navigation - no fallback needed
    const trainingId = course.training_id;

    navigate(`/course-details/${trainingId}`, {
      state: { course },
    });
  };

  return (
    <Box
      bg="#fff"
      borderRadius="16px"
      boxShadow="sm"
      border="1.5px solid #E6EAF0"
      overflow="hidden"
      w="100%"
      maxW="500px"
      m="auto"
      p={0}
      display="flex"
      flexDirection="column"
      h="100%"
    >
      {/* Top: Image */}
      <Image
        src={image}
        alt={title}
        pt={2}
        w="100%"
        h="210px"
        objectFit="contain"
        borderTopRadius="16px"
        flexShrink={0}
      />
      {/* Content */}
      <Box
        p={4}
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        {/* Tag & Free */}
        {/* <Flex align="center" mb={2} justify="space-between">
          <Badge bg="#F3F5F7" color="#235B63" borderRadius="8px" px={3} py={1} fontWeight="500" fontSize="sm">{tag || "Design"}</Badge>
          <Text color="#7A869A" fontWeight="500" fontSize="sm">{price || "Free"}</Text>
        </Flex> */}
        <Text
          fontWeight="bold"
          fontSize="lg"
          color="#1A202C"
          mb={2}
          lineHeight="1.3"
        >
          {course_name}
        </Text>
        {/* Title */}
        <Text
          fontWeight="normal"
          fontSize="sm"
          color="#1A202C"
          mb={2}
          lineHeight="1.3"
        >
          Batch Name: {title}
        </Text>
        <Text
          fontWeight="normal"
          fontSize="sm"
          color="#1A202C"
          mb={2}
          lineHeight="1.3"
          bg={
            batch_type_label === "Free" 
              ? "#91f0dd" 
              : "#FFF5E6"
          }
          px={0}
          py={1}
          borderRadius="4px"
          display="inline-block"
          w="fit-content"
        >
          Registration Fee: {batch_type_label === "Free" ? "Free" : amount}
        </Text>
        {/* Additional Info Row */}
        <Text
          fontWeight="normal"
          fontSize="sm"
          color="#1A202C"
          mb={2}
          lineHeight="1.3"
        >
          Duration: {formatDate(course.startDate)} to{" "}
          {formatDate(course.endDate)}
        </Text>

        {/* Instructor & Rating */}
        {/* <Flex align="center" mb={2} justify="space-between">
          <Flex align="center">
            <Avatar src={instructorAvatar} name={instructor} size="sm" />
            <Text color="#7A869A" fontWeight="600" fontSize="sm" ml={2}>{instructor}</Text>
          </Flex>
          <Flex align="center">
            <Icon as={FaStar} color="#F6B100" boxSize={4} />
            <Text color="#7A869A" fontWeight="500" fontSize="sm" ml={1}>{rating} ({reviews} Reviews)</Text>
          </Flex>
        </Flex> */}
        <Flex
          align="center"
          color="#235B63"
          fontSize="sm"
          justify="space-between"
        >
          <Flex align="center">
            <Icon as={FaRegClone} mr={1} />
            {batch_status}
          </Flex>
          <Flex align="center">
            <Icon as={FaRegClock} mr={1} />
            {totalHours}
          </Flex>
          <Flex align="center">
            <Icon as={FaRegUser} mr={1} />
            {venue}
          </Flex>
        </Flex>
      </Box>

      {/* Bottom: Deadline & Button */}
      <Flex
        align="center"
        justify="space-between"
        borderTop="1.5px solid #E6EAF0"
        bg="#F9FAFB"
        px={4}
        py={3}
        borderBottomRadius="16px"
      >
        <Box flex="1" minW="0">
          <Text fontWeight="bold" fontSize="md" color="#222" lineHeight="1.3">
            {formatDate(deadline)}
          </Text>
          <Text color="#7A869A" fontSize="xs" lineHeight="1.3">
            Registration Deadline
          </Text>
        </Box>
        <Button
          bg="#0F6849"
          borderRadius="8px"
          px={6}
          fontWeight="600"
          textColor="white"
          size="md"
          onClick={handleClick}
          _hover={{ bg: "#008b17" }}
          flexShrink={0}
          ml={3}
        >
          View
        </Button>
      </Flex>
    </Box>
  );
};

export default SkillCourseCard;
